import { Injectable, inject, computed, signal } from '@angular/core';
import { Rol } from '@shared/enums/rol.enum';
import { Cliente } from '@shared/models/cliente.model';
import { Sociedad } from '@shared/models/sociedad.model';
import { ClienteService } from '../cliente/cliente.service';
import { RolService } from '../rol/rol.service';
import { SociedadService } from '../sociedad/sociedad.service';
import { StorageService } from '../storage/storage.service';
import { NavController } from '@ionic/angular/standalone';

@Injectable({
  providedIn: 'root',
})
export class FiltroClienteService {
  private readonly _clientes = inject(ClienteService);
  private readonly _sociedades = inject(SociedadService);
  private readonly _rol = inject(RolService);
  private readonly _storage = inject(StorageService);
  private readonly _nav = inject(NavController);

  readonly userRol = computed(() => this._rol.userRol());
  readonly roles = Rol;

  readonly clienteSeleccionado = signal<Cliente | undefined>(undefined);
  readonly sociedadSeleccionada = signal<Sociedad | undefined>(undefined);
  readonly sociedades = signal<Sociedad[]>([]);
  readonly clientes = signal<Cliente[]>([]);

  async hayClienteSeleccionado(): Promise<boolean> {
    if (this.clienteSeleccionado()) return true;

    const clienteEnStorage = await this._storage.getItem('clienteSeleccionado');
    if (clienteEnStorage) {
      const sociedades = (await this._storage.getItem('sociedades')) as Sociedad[];
      this.sociedades.set(sociedades);
      this.clienteSeleccionado.set(clienteEnStorage);
      return true;
    }

    return false;
  }

  obtenerSociedades(): void {
    const rol = this.userRol()!;
    rol === Rol.SUPERADMIN ? this.buscarSociedades() : this.sociedadesEnStorage();
  }

  seleccionarSociedad(sociedad: Sociedad): void {
    this.clientes.set([]);
    this.sociedadSeleccionada.set(sociedad);
    this._storage.setItem('sociedadSeleccionada', sociedad);
    this.buscarClientes(sociedad);
  }

  seleccionarCliente(cliente: Cliente): void {
    this.clienteSeleccionado.set(cliente);
    this._storage.setItem('clienteSeleccionado', cliente);
    this._nav.navigateForward('/app/inicio');
  }

  cambiarCliente(): void {
    this.borrarCliente();
    this._nav.navigateRoot('/seleccionar-cliente');
  }

  borrarSociedad(): void {
    this.sociedadSeleccionada.set(undefined);
    this._storage.removeItem('sociedadSeleccionada');
  }

  borrarCliente(): void {
    this.clienteSeleccionado.set(undefined);
    this._storage.removeItem('clienteSeleccionado');
    if (this.sociedades().length > 1) {
      this.borrarSociedad();
    }
  }

  private async sociedadesEnStorage(): Promise<void> {
    const sociedades: Sociedad[] = await this._storage.getItem('sociedades');
    this.actualizarSociedades(sociedades);
  }

  private buscarSociedades(): void {
    this._sociedades.findAll().subscribe({
      next: (sociedades) => this.actualizarSociedades(sociedades),
    });
  }

  private actualizarSociedades(sociedades: Sociedad[]): void {
    this.sociedades.set(sociedades);
    if (sociedades.length === 1) {
      this.seleccionarSociedad(sociedades[0]);
    }
  }

  private buscarClientes({ id: idSociedad }: Sociedad): void {
    this._clientes.findFor({ idSociedad, habilitado: true }).subscribe({
      next: (clientes) => this.actualizarClientes(clientes),
    });
  }

  private actualizarClientes(clientes: Cliente[]): void {
    this.clientes.set(clientes);
    if (clientes.length === 1) {
      this.seleccionarCliente(clientes[0]);
    }
  }

  logout(): void {
    this.clienteSeleccionado.set(undefined);
    this.sociedadSeleccionada.set(undefined);
    this.sociedades.set([]);
    this.clientes.set([]);
    this._storage.removeItem('clienteSeleccionado');
    this._storage.removeItem('sociedadSeleccionada');
  }
}
